/*
 * User Appeals Portal
 * Allows suspended users to submit appeals, upload evidence, and track status
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, Upload, FileText, CheckCircle, XCircle, Clock, Trash2 } from "lucide-react";
import { format, differenceInDays } from "date-fns";

interface SubmitAppealForm {
  reason: string;
  details: string;
  files: File[];
}

export default function UserAppeals() {
  const { user } = useAuth();
  const [formData, setFormData] = useState<SubmitAppealForm>({
    reason: "",
    details: "",
    files: [],
  });
  const [uploadedFiles, setUploadedFiles] = useState<{ file: File; preview?: string }[]>([]);
  const [activeTab, setActiveTab] = useState("submit");

  // Fetch user's appeals
  const { data: appealsData, isLoading: appealsLoading, refetch: refetchAppeals } = (trpc.admin as any).appeals.getMyAppeals.useQuery(
    undefined,
    { placeholderData: { appeals: [] } }
  );

  // Fetch user's suspension status
  const { data: userSuspensionData } = trpc.auth.me.useQuery(undefined, {
    placeholderData: null,
  });

  const isSuspended = (userSuspensionData as any)?.suspensionStatus === "suspended";
  const isBanned = (userSuspensionData as any)?.suspensionStatus === "banned";

  // Submit appeal mutation
  const submitAppeal = (trpc.admin as any).appeals.submitAppeal.useMutation({
    onSuccess: () => {
      setFormData({ reason: "", details: "", files: [] });
      setUploadedFiles([]);
      refetchAppeals();
      alert("Appeal submitted successfully!");
      setActiveTab("history");
    },
    onError: (error: any) => {
      alert(`Error submitting appeal: ${error.message}`);
    },
  });

  // Withdraw appeal mutation
  const withdrawAppeal = (trpc.admin as any).appeals.withdrawAppeal.useMutation({
    onSuccess: () => {
      refetchAppeals();
      alert("Appeal withdrawn successfully!");
    },
    onError: (error: any) => {
      alert(`Error withdrawing appeal: ${error.message}`);
    },
  });

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedFiles((prev) => [
          ...prev,
          {
            file,
            preview: event.target?.result as string,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove uploaded file
  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle form submission
  const handleSubmitAppeal = async () => {
    if (!formData.reason || !formData.details) {
      alert("Please fill in all required fields");
      return;
    }

    if (uploadedFiles.length === 0) {
      alert("Please upload at least one piece of evidence");
      return;
    }

    // Upload files and submit appeal
    const evidenceUrls = uploadedFiles.map((f) => ({
      fileUrl: f.preview || "",
      fileName: f.file.name,
      description: "",
    }));

    await submitAppeal.mutateAsync({
      appealReason: formData.reason,
      appealDetails: formData.details,
      evidence: evidenceUrls,
    });
  };

  // Get appeal status badge
  const getStatusBadge = (status: string | undefined) => {
    const baseClass = "inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case "pending":
        return (
          <span className={`${baseClass} bg-yellow-100 text-yellow-800`}>
            <Clock size={14} /> Pending Review
          </span>
        );
      case "approved":
        return (
          <span className={`${baseClass} bg-green-100 text-green-800`}>
            <CheckCircle size={14} /> Approved
          </span>
        );
      case "denied":
        return (
          <span className={`${baseClass} bg-red-100 text-red-800`}>
            <XCircle size={14} /> Denied
          </span>
        );
      case "withdrawn":
        return (
          <span className={`${baseClass} bg-gray-100 text-gray-800`}>
            <AlertCircle size={14} /> Withdrawn
          </span>
        );
      default:
        return null;
    }
  };

  // Calculate days until appeal deadline
  const getDaysUntilDeadline = (createdAt: Date) => {
    const deadline = new Date(createdAt);
    deadline.setDate(deadline.getDate() + 30);
    return differenceInDays(deadline, new Date());
  };

  const appeals = useMemo(() => appealsData?.appeals || [], [appealsData]);
  const pendingAppeals = appeals.filter((a: any) => a.status === "pending");
  const resolvedAppeals = appeals.filter((a: any) => a.status !== "pending");

  if (!user) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading font-bold text-2xl mb-2">Please Log In</h1>
          <p className="text-black/60">You need to be logged in to access the appeals portal.</p>
        </div>
      </div>
    );
  }

  if (!isSuspended && !isBanned) {
    return (
      <div className="min-h-screen bg-white text-black py-12">
        <div className="container max-w-2xl">
          <Card className="border-2 border-black">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle size={24} className="text-green-600" />
                Account Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-black/70">
                Your account is currently active. You don't have any active suspensions or bans.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black py-12">
      <div className="container max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl mb-2">Appeal Your Suspension</h1>
          <p className="text-black/60">
            {isBanned
              ? "Your account has been permanently banned. You can submit an appeal for review."
              : "Your account has been suspended. Submit an appeal with supporting evidence to request reinstatement."}
          </p>
        </div>

        {/* Suspension Info Card */}
        {userSuspensionData && (
          <Card className="border-2 border-red-600 mb-8 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-600">Suspension Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-black/60">Reason</p>
                <p className="font-medium">{(userSuspensionData as any).suspensionReason || "Not specified"}</p>
              </div>
              <div>
                <p className="text-sm text-black/60">Suspended On</p>
                <p className="font-medium">
                  {(userSuspensionData as any).suspendedAt
                    ? format(new Date((userSuspensionData as any).suspendedAt), "PPP")
                    : "Unknown"}
                </p>
              </div>
              {(userSuspensionData as any).status === "suspended" && (
                <div className="bg-yellow-100 border border-yellow-600 rounded p-3 text-sm">
                  <p className="font-medium text-yellow-900">Appeal Deadline</p>
                  <p className="text-yellow-800">
                    You have 30 days from suspension to submit an appeal.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 border-2 border-black">
            <TabsTrigger value="submit" className="border-r-2 border-black">
              Submit Appeal
            </TabsTrigger>
            <TabsTrigger value="history">Appeal History</TabsTrigger>
          </TabsList>

          {/* Submit Appeal Tab */}
          <TabsContent value="submit" className="space-y-6">
            <Card className="border-2 border-black">
              <CardHeader>
                <CardTitle>Submit Your Appeal</CardTitle>
                <CardDescription>
                  Provide detailed information and supporting evidence for your appeal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Appeal Reason */}
                <div>
                  <label className="block text-sm font-medium mb-2">Appeal Reason *</label>
                  <select
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-black rounded font-mono text-sm"
                  >
                    <option value="">Select a reason...</option>
                    <option value="mistaken_identity">Mistaken Identity</option>
                    <option value="account_compromised">Account Compromised</option>
                    <option value="terms_misunderstanding">Misunderstood Terms</option>
                    <option value="technical_issue">Technical Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Appeal Details */}
                <div>
                  <label className="block text-sm font-medium mb-2">Detailed Explanation *</label>
                  <textarea
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    placeholder="Explain your situation in detail. Be specific and honest about what happened."
                    className="w-full px-4 py-3 border-2 border-black rounded font-mono text-sm min-h-32 resize-none"
                  />
                  <p className="text-xs text-black/50 mt-1">Minimum 50 characters required</p>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Supporting Evidence *</label>
                  <div className="border-2 border-dashed border-black rounded-lg p-6 text-center cursor-pointer hover:bg-black/5 transition">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx"
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload size={32} className="mx-auto mb-2 text-black/60" />
                      <p className="font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-black/50 mt-1">PNG, JPG, PDF, DOC up to 10MB each</p>
                    </label>
                  </div>

                  {/* Uploaded Files Preview */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium">Uploaded Files ({uploadedFiles.length})</p>
                      {uploadedFiles.map((item: { file: File; preview?: string }, index: number) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border border-black/20 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <FileText size={18} />
                            <span className="text-sm truncate">{item.file.name}</span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="p-1 hover:bg-red-100 rounded transition"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <Button
                    onClick={handleSubmitAppeal}
                    disabled={submitAppeal.isPending || !formData.reason || !formData.details || uploadedFiles.length === 0}
                    className="flex-1 bg-black text-white font-heading font-bold py-3 hover:bg-black/90 disabled:opacity-50"
                  >
                    {submitAppeal.isPending ? "Submitting..." : "Submit Appeal"}
                  </Button>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> Your appeal will be reviewed by our team within 7-14 days. You'll receive
                    an email notification when a decision is made.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appeal History Tab */}
          <TabsContent value="history" className="space-y-6">
            {appealsLoading ? (
              <Card className="border-2 border-black">
                <CardContent className="py-12 text-center">
                  <p className="text-black/60">Loading your appeals...</p>
                </CardContent>
              </Card>
            ) : appeals.length === 0 ? (
              <Card className="border-2 border-black">
                <CardContent className="py-12 text-center">
                  <AlertCircle size={32} className="mx-auto mb-2 text-black/40" />
                  <p className="text-black/60">You haven't submitted any appeals yet.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Pending Appeals */}
                {pendingAppeals.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4">Pending Appeals</h3>
                    <div className="space-y-4">
                      {pendingAppeals.map((appeal: any) => (
                        <Card key={appeal.id} className="border-2 border-yellow-600">
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg">{appeal.appealReason}</CardTitle>
                                <CardDescription>
                                  Submitted on {format(new Date(appeal.createdAt), "PPP")}
                                </CardDescription>
                              </div>
                              {getStatusBadge(appeal.status)}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <p className="text-sm text-black/60">Your Appeal</p>
                              <p className="text-sm">{appeal.appealDetails}</p>
                            </div>
                            {appeal.evidence && appeal.evidence.length > 0 && (
                              <div>
                                <p className="text-sm font-medium mb-2">Evidence ({appeal.evidence.length} files)</p>
                                <div className="space-y-1">
                                  {appeal.evidence?.map((file: any, idx: number) => (
                                    <p key={idx} className="text-xs text-black/60 flex items-center gap-1">
                                      <FileText size={12} /> {file.fileName}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                              <p className="font-medium text-yellow-900">
                                {getDaysUntilDeadline(new Date(appeal.createdAt))} days remaining to update your appeal
                              </p>
                            </div>
                            <Button
                              onClick={() => withdrawAppeal.mutateAsync({ appealId: appeal.id })}
                              disabled={withdrawAppeal.isPending}
                              variant="outline"
                              className="w-full border-2 border-red-600 text-red-600 hover:bg-red-50"
                            >
                              Withdraw Appeal
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolved Appeals */}
                {resolvedAppeals.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-xl mb-4">Appeal History</h3>
                    <div className="space-y-4">
                      {resolvedAppeals.map((appeal: any) => (
                        <Card
                          key={appeal.id}
                          className={`border-2 ${
                            appeal.status === "approved"
                              ? "border-green-600"
                              : appeal.status === "denied"
                                ? "border-red-600"
                                : "border-gray-600"
                          }`}
                        >
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-lg">{appeal.appealReason}</CardTitle>
                                <CardDescription>
                                  Submitted on {format(new Date(appeal.createdAt), "PPP")}
                                  {appeal.reviewedAt && ` • Reviewed on ${format(new Date(appeal.reviewedAt), "PPP")}`}
                                </CardDescription>
                              </div>
                              {getStatusBadge(appeal.status)}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <p className="text-sm text-black/60">Your Appeal</p>
                              <p className="text-sm">{appeal.appealDetails}</p>
                            </div>
                            {appeal.reviewNotes && (
                              <div className="bg-black/5 border border-black/20 rounded p-3">
                                <p className="text-xs font-medium text-black/60 mb-1">Admin Review Notes</p>
                                <p className="text-sm">{appeal.reviewNotes}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
